import { cx } from "@frontend/components/ui";

function Bone({ className }: { className?: string }) {
  return <div className={cx("animate-pulse rounded-lg bg-ink/[0.06]", className)} />;
}

/** Full-page sketch loader — used only on the first fetch, not soft refreshes. */
export function PageSkeleton({
  variant = "page",
}: {
  variant?: "page" | "workspace" | "inbox" | "settings" | "creator";
}) {
  if (variant === "workspace") {
    return (
      <div className="flex min-h-dvh flex-col bg-canvas" aria-busy="true" aria-label="Loading">
        <div className="border-b border-line/70 px-4 py-3">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <Bone className="h-8 w-28" />
            <Bone className="h-8 w-48" />
            <Bone className="h-8 w-24" />
          </div>
        </div>
        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col space-y-4 px-4 py-8">
          <Bone className="h-8 w-40" />
          <Bone className="h-4 w-64" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Bone className="h-28" />
            <Bone className="h-28" />
            <Bone className="h-28" />
            <Bone className="h-28" />
          </div>
          <Bone className="min-h-[14rem] flex-1" />
        </div>
        <div className="border-t border-line/70 px-4 py-5">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <Bone className="h-6 w-32" />
            <Bone className="h-6 w-40" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "inbox") {
    return (
      <div
        className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-1 flex-col px-4 py-4"
        aria-busy="true"
        aria-label="Loading"
      >
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-line bg-surface">
          <div className="w-full shrink-0 space-y-2 p-3 lg:w-[17rem] lg:border-r lg:border-line">
            <Bone className="mb-3 h-8 w-28" />
            <Bone className="h-14" />
            <Bone className="h-14" />
            <Bone className="h-14" />
            <Bone className="h-14" />
            <Bone className="h-14" />
          </div>
          <div className="hidden min-w-0 flex-1 flex-col gap-3 p-6 lg:flex">
            <Bone className="h-6 w-48" />
            <Bone className="h-4 w-72" />
            <Bone className="mt-4 min-h-[12rem] flex-1" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "settings") {
    return (
      <div
        className="mx-auto w-full max-w-6xl flex-1 px-4 py-8"
        aria-busy="true"
        aria-label="Loading"
      >
        <Bone className="mb-2 h-8 w-36" />
        <Bone className="mb-8 h-4 w-56" />
        <div className="grid gap-5 lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-10">
          <div className="space-y-2">
            <Bone className="h-10" />
            <Bone className="h-10" />
            <Bone className="h-10" />
          </div>
          <div className="space-y-3">
            <Bone className="h-40" />
            <Bone className="h-24" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "creator") {
    return (
      <div
        className="mx-auto w-full max-w-6xl flex-1 px-4 py-8"
        aria-busy="true"
        aria-label="Loading"
      >
        <div className="grid gap-5 lg:grid-cols-[1fr_36rem]">
          <div className="space-y-4">
            <div className="flex gap-3">
              <Bone className="size-14 shrink-0 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Bone className="h-6 w-40" />
                <Bone className="h-4 w-24" />
              </div>
            </div>
            <Bone className="h-28" />
            <Bone className="h-20" />
          </div>
          <Bone className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="mx-auto w-full max-w-6xl flex-1 space-y-4 px-4 py-8"
      aria-busy="true"
      aria-label="Loading"
    >
      <Bone className="h-8 w-40" />
      <Bone className="h-4 w-64" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Bone className="h-28" />
        <Bone className="h-28" />
        <Bone className="h-28" />
        <Bone className="h-28" />
      </div>
      <Bone className="min-h-[14rem] flex-1" />
    </div>
  );
}
